/**
 * <cw-header>: fills the header with the delivery words, keeps the basket count, and turns
 * taps into requests the sheets and panels answer (design-handoff/components/header.md).
 *
 * Each button with data-open="x" dispatches a cancelable 'cw:x:open' on document. A sheet or
 * panel that exists claims it with preventDefault(); until then the page falls back to the
 * nearest working thing (the /cart page, the account page, the search page).
 */
import { cart } from '@cw/cart';
import { view } from '@cw/delivery';

const FALLBACK = {
  basket: () => `${window.Shopify?.routes?.root || '/'}cart`,
  account: () => '/account',
  orderagain: () => '/account',
  search: () => `${window.Shopify?.routes?.root || '/'}search`,
};

class CwHeader extends HTMLElement {
  connectedCallback() {
    this.dataset.ready = '';
    this.addEventListener('click', this.#onClick);
    document.addEventListener('cw:delivery:change', this.#paint);
    document.addEventListener('cw:cart:change', this.#count);
    document.addEventListener('cw:fold', this.#fold);
    this.#paint({ detail: view() });
    this.#count();
    this.#categories();
    this.#measure();
    document.fonts?.ready.then(this.#measure);
    window.addEventListener('resize', this.#measure, { passive: true });
  }

  /** The phone's delivery row folds by its real height (cw-header.css --cw-hdr-m-h). */
  #measure = () => {
    const row = this.querySelector('.cw-hdr-m');
    if (row?.scrollHeight) this.style.setProperty('--cw-hdr-m-h', `${row.scrollHeight}px`);
  };

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    document.removeEventListener('cw:delivery:change', this.#paint);
    document.removeEventListener('cw:cart:change', this.#count);
    document.removeEventListener('cw:fold', this.#fold);
    window.removeEventListener('resize', this.#measure);
  }

  #paint = (event) => {
    const v = event.detail || view();
    this.querySelectorAll('[data-cw="line1"]').forEach((el) => (el.textContent = v.line1));
    this.querySelectorAll('[data-cw="line2"]').forEach((el) => (el.textContent = v.line2));
    this.querySelectorAll('[data-cw="label"]').forEach((el) => (el.textContent = v.label));
    this.dataset.tier = v.tier || 'near';
  };

  #count = () => {
    const n = cart.count;
    for (const el of this.querySelectorAll('[data-cw="count"]')) {
      el.hidden = n === 0;
      el.textContent = String(n);
    }
  };

  /** "Categories" opens the aisle the shopper was last in, else the first one. */
  #categories() {
    let last = null;
    try {
      last = sessionStorage.getItem('cw:last-aisle');
    } catch {
      last = null;
    }
    if (!last) return;
    for (const link of this.querySelectorAll('[data-cw="categories"]')) link.href = last;
  }

  /** The aisle list folds the shelf title and tucks the phone's delivery row away. */
  #fold = (event) => {
    this.classList.toggle('is-tucked', !!event.detail?.folded);
  };

  #onClick = (event) => {
    const button = /** @type {HTMLElement} */ (event.target).closest('[data-open]');
    if (!button || !this.contains(button)) return;
    // The desktop search field takes typing; only its focus is an "open".
    if (button.tagName === 'INPUT') return;
    const name = button.dataset.open;
    const request = new CustomEvent(`cw:${name}:open`, { cancelable: true, detail: { source: button } });
    document.dispatchEvent(request);
    if (!request.defaultPrevented && FALLBACK[name]) window.location.href = FALLBACK[name]();
  };
}

if (!customElements.get('cw-header')) customElements.define('cw-header', CwHeader);
